/**
 * KnowledgePainPointsTile — Tier C Knowledge Labs.
 *
 * Shows the top pain points identified by the knowledge pipeline with
 * their full description and narrative text. Polls every 60 seconds.
 *
 * Auth: Admin JWT (standard apiFetch).
 *
 * Endpoint: GET /api/v1/admin/knowledge/pain-points
 */

import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../../../../api/http';

// ── Types ─────────────────────────────────────────────────────────────────────

interface PainPoint {
  pain_id: string;
  description: string;
  narrative: string;
  category: string;
  source: string;
  frequency_score: number;
  severity_score: number;
  addressability_score: number;
  composite_score: number;
  auto_approve: boolean;
  discovered_at: number;
}

interface PainPointsResponse {
  pain_points: PainPoint[];
  total: number;
}

// ── Fetch ─────────────────────────────────────────────────────────────────────

async function fetchPainPoints(): Promise<PainPointsResponse> {
  return apiFetch<PainPointsResponse>('/admin/knowledge/pain-points');
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Map a composite score to a severity label. */
function severityLabel(score: number): string {
  if (score >= 0.8) return 'critical';
  if (score >= 0.6) return 'high';
  if (score >= 0.4) return 'medium';
  return 'low';
}

/** Color token for a severity label. */
function severityColor(severity: string): string {
  switch (severity) {
    case 'critical':
      return 'var(--omega-state-refused)';
    case 'high':
      return 'var(--omega-state-degraded)';
    case 'medium':
      return 'var(--omega-state-degraded)';
    default:
      return 'var(--omega-ink-subtle)';
  }
}

/** Humanise a snake_case category name. */
function humaniseCategory(cat: string): string {
  return cat
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// ── Sub-components ────────────────────────────────────────────────────────────

function DomainBadge({ category }: { category: string }) {
  return (
    <span
      style={{
        fontSize: '8pt',
        padding: '1px 6px',
        border: '1px solid var(--omega-rim-strong)',
        color: 'var(--omega-ink-muted)',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        fontWeight: 600,
        flexShrink: 0,
      }}
    >
      {humaniseCategory(category)}
    </span>
  );
}

function SeverityBadge({ score }: { score: number }) {
  const label = severityLabel(score);
  const color = severityColor(label);
  return (
    <span
      style={{
        fontSize: '8pt',
        padding: '1px 6px',
        border: `1px solid ${color}`,
        color,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        fontWeight: 600,
        flexShrink: 0,
      }}
    >
      {label}
    </span>
  );
}

function PainPointRow({ point }: { point: PainPoint }) {
  return (
    <div
      style={{
        padding: 'var(--omega-gap-2) 0',
        borderTop: '1px solid var(--omega-rim)',
      }}
    >
      {/* Badges row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          marginBottom: 6,
          flexWrap: 'wrap',
        }}
      >
        <DomainBadge category={point.category} />
        <SeverityBadge score={point.composite_score} />
        <span
          style={{
            marginLeft: 'auto',
            fontSize: '8pt',
            color: 'var(--omega-ink-subtle)',
            letterSpacing: '0.04em',
          }}
        >
          {point.source.replace(/_/g, ' ')}
        </span>
      </div>

      {/* Description */}
      <div
        style={{
          fontSize: '10pt',
          color: 'var(--omega-ink)',
          lineHeight: 1.5,
          marginBottom: point.narrative ? 6 : 0,
        }}
      >
        {point.description}
      </div>

      {/* Narrative */}
      {point.narrative && (
        <div
          style={{
            fontSize: '9pt',
            color: 'var(--omega-ink-muted)',
            lineHeight: 1.5,
            fontStyle: 'italic',
          }}
        >
          {point.narrative}
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function KnowledgePainPointsTile() {
  const { data, isPending, isError } = useQuery<PainPointsResponse>({
    queryKey: ['omega', 'knowledge', 'pain-points'],
    queryFn: fetchPainPoints,
    refetchInterval: 60_000,
    staleTime: 55_000,
    retry: 1,
  });

  return (
    <section
      aria-labelledby="pain-points-heading"
      style={{
        background: 'var(--omega-surface)',
        border: '1px solid var(--omega-rim)',
        padding: 'var(--omega-gap-3)',
        marginTop: 'var(--omega-gap-3)',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 'var(--omega-gap-2)',
          marginBottom: 'var(--omega-gap-2)',
        }}
      >
        <h2
          id="pain-points-heading"
          style={{
            margin: 0,
            fontSize: '11pt',
            fontWeight: 700,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: 'var(--omega-ink)',
          }}
        >
          KNOWLEDGE LABS — Pain Points
        </h2>
        {data != null && (
          <span
            style={{
              fontSize: '9pt',
              padding: '1px 7px',
              border: '1px solid var(--omega-rim-strong)',
              color: 'var(--omega-ink-muted)',
              letterSpacing: '0.06em',
              fontWeight: 600,
            }}
          >
            {data.total} indexed
          </span>
        )}
      </div>

      {/* Loading */}
      {isPending && (
        <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '10pt' }}>
          Loading&hellip;
        </div>
      )}

      {/* Error */}
      {isError && (
        <div style={{ color: 'var(--omega-state-refused)', fontSize: '10pt' }}>
          Failed to load pain points.
        </div>
      )}

      {/* Empty state */}
      {!isPending && !isError && data != null && data.pain_points.length === 0 && (
        <div
          style={{
            color: 'var(--omega-ink-subtle)',
            fontSize: '10pt',
            fontStyle: 'italic',
            letterSpacing: '0.04em',
          }}
        >
          No pain points indexed yet.
        </div>
      )}

      {/* Pain point list */}
      {!isPending && !isError && data != null && data.pain_points.length > 0 && (
        <div>
          {data.pain_points.slice(0, 5).map((point) => (
            <PainPointRow key={point.pain_id} point={point} />
          ))}
        </div>
      )}
    </section>
  );
}
